export default class MobileNav {
    constructor(root, options = {}) {
        this.root = root;
        this.options = options;
        this.isOpen = false;
        this.processedListCount = 0;
        this.totalListCount = 0;

        if (!this.root) {
            console.error('Invalid element specified for mobile nav root');
        } else {
            this._init();
        }
    }

    _init() {
        this._debug('Initialising mobile nav');

        // Append extra items to root
        this._appendToRootList();

        // Process lists
        this._debug('Started processing lists');
        let promise = new Promise((resolve, reject) => {
            let allLists = this.root.querySelectorAll('ul');
            this.totalListCount = allLists.length;

            let topLevelList = this.root.querySelector('ul');
            this._processList(topLevelList);

            if (this.processedListCount == this.totalListCount) {
                resolve("Completed processing lists");
            } else {
                reject(Error("Error processing lists"));
            }
        });
        promise.then(result => {
            this._debug('Finished processing lists');
            this._showList(this._findActiveListId());
            this._unnestLists();
        }, err => console.error(err));

        // Add site overlay
        const siteOverlay = document.createElement('div');
        siteOverlay.classList.add('mobile-nav__site-overlay');
        siteOverlay.addEventListener('click', (e) => {
            e.preventDefault();
            this._toggleMobileNav();
        });
        document.body.append(siteOverlay);

        // Register mobile nav triggers and actions
        let toggleSelector = this._getOption('toggleSelector');
        if (toggleSelector) {
            let toggleTriggers = document.querySelectorAll(toggleSelector);
            toggleTriggers.forEach((toggleTrigger) => {
                toggleTrigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    this._toggleMobileNav();
                });
                this._debug('Registered toggle trigger');
            });
        }
        let openSelector = this._getOption('openSelector');
        if (openSelector) {
            let openTriggers = document.querySelectorAll(openSelector);
            openTriggers.forEach((openTrigger) => {
                openTrigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    this._openMobileNav();
                });
                this._debug('Registered open trigger');
            });
        }
        let closeSelector = this._getOption('closeSelector');
        if (closeSelector) {
            let closeTriggers = document.querySelectorAll(closeSelector);
            closeTriggers.forEach((closeTrigger) => {
                closeTrigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    this._closeMobileNav();
                });
                this._debug('Registered close trigger');
            });
        }
    }

    _getOption(optionName) {
        if (Object.hasOwn(this.options, optionName)) {
            return this.options[optionName];
        }
        return null;
    }

    _beforeOpen() {
        this._debug('Before open');
        let customBeforeOpen = this._getOption('beforeOpen');
        if (typeof (customBeforeOpen) === 'function') {
            customBeforeOpen();
        }
    }

    _beforeClose() {
        this._debug('Before close');
        let customBeforeClose = this._getOption('beforeClose');
        if (typeof (customBeforeClose) === 'function') {
            customBeforeClose();
        }
    }

    _openMobileNav() {
        this._beforeOpen();
        this._debug('Open mobile nav');
        this._showList(this._findActiveListId());
        this.isOpen = true;
        document.body.classList.remove('mobile-nav-open', 'mobile-nav-closed', 'mobile-nav-closing');
        document.body.classList.add('mobile-nav-opening');
        setTimeout(() => {
            document.body.classList.add('mobile-nav-open');
            document.body.classList.remove('mobile-nav-opening');
        });
    }

    _closeMobileNav() {
        this._beforeClose();
        this._debug('Close mobile nav');
        let transitionDuration = parseInt(this._getOption('transitionDuration'));
        if (!transitionDuration) {
            transitionDuration = 300;
        }
        this.isOpen = false;
        document.body.classList.remove('mobile-nav-opening', 'mobile-nav-open', 'mobile-nav-closed');
        document.body.classList.add('mobile-nav-closing');
        setTimeout(() => {
            document.body.classList.add('mobile-nav-closed');
            document.body.classList.remove('mobile-nav-closing');
        }, transitionDuration);
    }

    _toggleMobileNav() {
        this._debug('Toggle mobile nav');
        if (this.isOpen) {
            this._closeMobileNav();
        } else {
            this._openMobileNav();
        }
    }

    _appendToRootList() {
        this._debug('Append extra items to root list');
        const appendToRootListSelectors = this._getOption('appendToRootList');
        const appendItems = [];
        if (appendToRootListSelectors) {
            appendToRootListSelectors.forEach((selector) => {
                let matchingItems = this.root.querySelectorAll(selector);
                if (matchingItems.length > 0) {
                    matchingItems.forEach((item) => {
                        appendItems.push(item);
                    });
                }
            });
            if (appendItems.length > 0) {
                const rootList = this.root.querySelector(':scope > ul');
                if (rootList) {
                    let appendedCount = 0;
                    appendItems.forEach((appendItem) => {
                        let appendItemLi = document.createElement('li');
                        appendItemLi.classList.add('mobile-nav__additional-item');
                        if (appendedCount == 0) {
                            appendItemLi.classList.add('mobile-nav__additional-item--first');
                        }
                        appendItemLi.append(appendItem);
                        rootList.append(appendItemLi);
                        appendedCount++;
                    });
                }
            }
        }
    }

    _processList(listEl, parentId = null, parentLinkText = null, parentLinkHref = null) {
        this._debug('Processing list');
        this.processedListCount++;
        let listId = this.processedListCount;
        listEl.dataset.mobileNavId = listId;

        let listItems = listEl.querySelectorAll(':scope > li');
        if (listItems.length > 0) {
            listItems.forEach((itemEl) => {
                let itemATag = itemEl.querySelector(':scope > a');
                let childListEl = itemEl.querySelector(':scope > ul');
                if (childListEl) {
                    itemEl.classList.add('mobile-nav__has-child');
                    let childListId = this._processList(childListEl, listId, itemATag.innerHTML, itemATag.href);
                    itemEl.dataset.mobileNavChildId = childListId;
                    itemEl.dataset.mobileNavHasChild = true;
                    itemATag.addEventListener('click', (e) => {
                        e.preventDefault();
                        listEl.classList.add('mobile-nav__higher-level');
                        this._showList(childListId);
                    })
                }
            });
        }

        if (parentId !== null) {
            // set parent ID data attribute
            listEl.dataset.mobileNavParentId = parentId;

            // prepend parent page link
            if (parentLinkText && parentLinkHref) {
                let parentLink = document.createElement('a');
                let parentLinkItem = document.createElement('li');
                parentLink.innerHTML = parentLinkText;
                parentLink.href = parentLinkHref;
                parentLinkItem.classList.add('mobile-nav__parent-link');
                parentLinkItem.append(parentLink);
                listEl.prepend(parentLinkItem);
            }

            // append back link
            let backLink = document.createElement('a');
            let backLinkItem = document.createElement('li');
            backLink.innerHTML = 'Back';
            backLink.href = '#';
            backLink.addEventListener('click', (e) => {
                e.preventDefault();
                this._showList(parentId);
            });
            backLinkItem.classList.add('mobile-nav__back-link');
            backLinkItem.append(backLink);
            listEl.append(backLinkItem);
        }

        return listId;
    }

    _unnestLists() {
        this._debug('Unnesting lists');
        let allLists = this.root.querySelectorAll('ul');
        allLists.forEach((listEl) => {
            this.root.append(listEl);
        });
    }

    _debug(message) {
        if (this._getOption('debug')) {
            console.log(message);
        }
    }

    _showList(listId) {
        let allLists = this.root.querySelectorAll('ul');
        allLists.forEach((listEl) => {
            if (listEl.dataset.mobileNavId == listId) {
                listEl.classList.remove('mobile-nav__higher-level');
                listEl.classList.add('mobile-nav__active-list');
                listEl.scrollTop = 0;
            } else {
                listEl.classList.remove('mobile-nav__active-list');
            }
        });
    }

    _findActiveListId() {
        let currentPageSelector = this._getOption('currentPageSelector');
        if (!currentPageSelector) {
            currentPageSelector = '.current-menu-item';
        }
        let currentPageItem = this.root.querySelector(currentPageSelector);
        if (currentPageItem) {
            let currentPageList = currentPageItem.parentElement;
            if (Object.hasOwn(currentPageList.dataset, 'mobileNavId')) {
                return currentPageList.dataset.mobileNavId;
            }
        } else {
            let topLevelList = this.root.querySelector('ul');
            return topLevelList.dataset.mobileNavId;
        }
        return null;
    }
}